import AddIcon from '@mui/icons-material/Add';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import HomeIcon from '@mui/icons-material/Home';
import WifiIcon from '@mui/icons-material/Wifi';
import format from 'date-fns/format';
import getDay from 'date-fns/getDay';
import { ptBR } from 'date-fns/locale';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import dayjs from 'dayjs';
import 'moment/locale/pt-br';
import { useCallback, useMemo, useState } from 'react';
import {
  Calendar as BigReactCalendar,
  EventPropGetter,
  NavigateAction,
  View,
  dateFnsLocalizer,
} from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { rrulestr } from 'rrule';
import { useAppSelector } from '../../hooks';
import { ClassEvent, PlaceType, UserRole } from '../../services/api.types';
import { TeacherBusyModel } from '../../services/teacher-busy.types';
import { darkenColor, formatClassTitleColor } from '../../utils/colors';
import { getEndHour } from '../../utils/date';
import { makeLevelLabel } from '../CalendarEvent';

// Calendar documentation: http://jquense.github.io/react-big-calendar/examples/index.html
// recurrence: https://github.com/jquense/react-big-calendar/issues/51

export type ClassOrTeacherBusyType = ClassEvent | TeacherBusyModel;
const mapEventStyle = {
  fontSize: '12px',
};
const mapEventIcon: Record<PlaceType, JSX.Element> = {
  REMOTE: <WifiIcon style={mapEventStyle} />,
  PLUS_HEADQUARTER: <AddIcon style={mapEventStyle} />,
  STUDENT_HOME: <HomeIcon style={mapEventStyle} />,
};

const getPlaceTypeIcon = (event: CalendarEvents): JSX.Element => {
  if (!event.isClass) return <DoNotDisturbIcon style={mapEventStyle} />;

  return mapEventIcon[event.placeType];
};

export type CalendarEvents = {
  title: string;
  start: Date;
  end: Date;
} & ClassOrTeacherBusyType;

interface CalendarProps {
  onSelectEvent?: (slotInfo: CalendarEvents) => void;
  onDateChange?: (date: Date) => void;
  events: ClassEvent[];
  teacherBusyEvents?: TeacherBusyModel[];
  date: Date;
}

const messages = {
  date: 'Data',
  time: 'Hora',
  event: 'Evento',
  allDay: 'Dia todo',
  week: 'Semana',
  work_week: 'Semana de trabalho',
  day: 'Dia',
  month: 'Mês',
  previous: 'Anterior',
  next: 'Próximo',
  yesterday: 'Ontem',
  tomorrow: 'Amanhã',
  today: 'Hoje',
  agenda: 'Agenda',
  noEventsInRange: 'Não há eventos no período selecionado',
  showMore: (total: number) => `+${total} mais`,
};

const locales = {
  'pt-BR': ptBR,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const makeClassTitle = (
  {
    subject,
    teacher,
    student,
    userRoleCalendar,
  }: Pick<ClassEvent, 'subject' | 'teacher' | 'student' | 'userRoleCalendar'>,
  userRole?: UserRole
): string => {
  const subjectLabel = `Aula: ${makeLevelLabel[subject.level](subject.name)}`;
  const teacherLabel = `Professor: ${teacher.displayName}`;
  const studentLabel = `Aluno: ${student.displayName}`;

  switch (userRole) {
    case UserRole.STUDENT:
    case UserRole.RESPONSIBLE:
      return `${subjectLabel} - ${teacher.displayName}`;
    case UserRole.TEACHER:
      return `${studentLabel} - ${subjectLabel}`;
    case UserRole.ADMIN:
    case UserRole.EMPLOYEE:
      return userRoleCalendar === UserRole.TEACHER
        ? `${studentLabel} - ${teacherLabel} - ${subjectLabel}`
        : `${teacherLabel} - ${studentLabel} - ${subjectLabel}`;
    default:
      return 'Não encontrado!';
  }
};

export const Calendar = ({
  events,
  teacherBusyEvents = [],
  onSelectEvent,
  onDateChange,
  date,
}: CalendarProps) => {
  const [viewDate, setViewDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [lastViewedWeek, setLastViewedWeek] = useState<Date | null>(null);
  const user = useAppSelector((state) => state.user);
  const userRole = user?.role;

  // Track current view
  const [currentView, setCurrentView] = useState<View>(handleCalendarView());

  // Calculate agendaDaysLimit as the number of days in the month of viewDate
  const agendaDaysLimit = useMemo(() => {
    if (currentView === 'agenda') {
      const startOfMonth = dayjs(viewDate).startOf('month');
      const endOfMonth = dayjs(viewDate).endOf('month');
      return endOfMonth.diff(startOfMonth, 'day') + 1;
    }
    return dayjs(viewDate).endOf('month').diff(viewDate, 'day');
  }, [viewDate, currentView]);

  const scrollToTime = dayjs().subtract(2, 'hour').toDate();

  // Helper to get start of week for a date
  const getStartOfWeek = (date: Date) => startOfWeek(date, { weekStartsOn: 0 });

  // Update selectedDate and lastViewedWeek on navigation
  const handleNavigate = (
    newDate: Date,
    view: View,
    action: NavigateAction
  ) => {
    if (view === 'agenda') {
      let nextDate = dayjs(viewDate);
      if (action === 'NEXT') {
        nextDate = nextDate.add(1, 'month').startOf('month');
      } else if (action === 'PREV') {
        nextDate = nextDate.subtract(1, 'month').startOf('month');
      } else {
        nextDate = dayjs(newDate);
      }
      setViewDate(nextDate.toDate());
      onDateChange?.(nextDate.toDate());
      return;
    }
    setViewDate(newDate);
    if (view === 'day') {
      setSelectedDate(newDate);
    } else if (view === 'week') {
      setLastViewedWeek(getStartOfWeek(newDate));
    }
    onDateChange?.(newDate);
  };

  // Handle view change and restore context
  const handleView = (view: View) => {
    setCurrentView(view);
    if (view === 'day') {
      if (selectedDate) {
        setViewDate(selectedDate);
      }
    } else if (view === 'week') {
      if (lastViewedWeek) {
        setViewDate(lastViewedWeek);
      }
    } else if (view === 'agenda') {
      // Always set to first day of current month when switching to agenda
      setViewDate(dayjs(viewDate).startOf('month').toDate());
    }
    // For month, keep current viewDate
  };

  // When selecting an event, update selectedDate
  const handleSelectEvent = (event: CalendarEvents) => {
    setSelectedDate(event.start);
    onSelectEvent?.(event);
  };

  // Set default view based on window width
  function handleCalendarView() {
    const width = window.innerWidth;
    if (width < 768) return 'day';
    return 'week';
  }

  const makeTitle = useCallback(
    (event: ClassOrTeacherBusyType) =>
      event.isClass
        ? makeClassTitle(event, userRole)
        : `${event.teacher.displayName} - Indisponível`,
    [userRole]
  );

  const buildCalendarEvent = useCallback(
    <T extends ClassOrTeacherBusyType>(events: T[]): CalendarEvents[] => {
      if (!events.length) return [];

      const start = new Date(date);
      start.setMonth(date.getMonth() - 1, 1);

      const end = new Date(date);
      end.setMonth(date.getMonth() + 2, 1);

      return events.reduce((acc, event) => {
        const rule = rrulestr(event.rrule, { forceset: true });
        const occurrences = rule.between(start, end);

        const eventOccurrences = occurrences.map((startHour) => {
          const title = makeTitle(event);
          const endHour = getEndHour(startHour, event.duration);

          const ce: CalendarEvents = {
            ...event,
            title,
            start: startHour,
            end: endHour,
          };
          return ce;
        });
        return acc.concat(eventOccurrences);
      }, [] as CalendarEvents[]);
    },
    [date, makeTitle]
  );

  const calendarEvents = useMemo(
    () => buildCalendarEvent(events),
    [buildCalendarEvent, events]
  );

  const backgroundEvents = useMemo(
    () => buildCalendarEvent(teacherBusyEvents),
    [buildCalendarEvent, teacherBusyEvents]
  );

  return (
    <BigReactCalendar
      className="bg-white rounded-lg shadow-lg p-4 "
      defaultView={handleCalendarView()}
      events={calendarEvents}
      backgroundEvents={backgroundEvents}
      onSelectEvent={handleSelectEvent}
      localizer={localizer}
      culture="pt-BR"
      startAccessor="start"
      endAccessor="end"
      showMultiDayTimes
      messages={messages}
      onNavigate={handleNavigate}
      onView={handleView}
      scrollToTime={scrollToTime}
      date={viewDate}
      length={agendaDaysLimit} // Limit the number of days in the agenda view
      components={{
        agenda: {
          event: ({ event }) => (
            <div className="flex gap-2 items-center">
              <div
                className="w-4 h-4 rounded-full shrink-0 text-black"
                style={{ backgroundColor: event.color }}
              ></div>
              <div>
                <span>{event.title}</span>
              </div>
            </div>
          ),
        },
        month: {
          event: ({ event }) => (
            <div className="flex items-center">
              <div className="w-0">
                <span>{event.title}</span>
              </div>
            </div>
          ),
        },
        week: {
          event: ({ event }) => (
            <div className="relative">
              <div className="flex bg-white absolute rounded-full justify-center items-center w-3.5 h-3.5 -left-1 -top-3.5 text-black">
                {getPlaceTypeIcon(event)}
              </div>
              <div className="flex">
                <div>
                  <span
                    style={{
                      color: formatClassTitleColor(event.color),
                    }}
                  >
                    {event.title}
                  </span>
                </div>
              </div>
            </div>
          ),
        },
        day: {
          event: ({ event }) => (
            <div className="flex relative">
              <div className="flex bg-white absolute rounded-full justify-center items-center w-3.5 h-3.5 -left-1 -top-3.5 text-black">
                {getPlaceTypeIcon(event)}
              </div>
              <div>
                <span
                  style={{
                    color: formatClassTitleColor(event.color),
                  }}
                >
                  {event.title}
                </span>
              </div>
            </div>
          ),
        },
      }}
      eventPropGetter={(event) => {
        const eventProps: ReturnType<EventPropGetter<CalendarEvents>> = {
          style: {
            backgroundColor: event.color,
            color: formatClassTitleColor(event.color),
          },
        };

        if (!event.isClass) {
          const darkColor = darkenColor(event.color, 50);
          eventProps.style = {
            ...eventProps.style,
            background: `repeating-linear-gradient(-45deg, ${event.color}, ${event.color} 10px, ${darkColor} 10px, ${darkColor} 20px)`,
          };
        }

        return eventProps;
      }}
    />
  );
};
